import { cx } from "./ui";

/**
 * A row of mutually exclusive filters, with "All" as the resting state.
 *
 * A `<select>` for a set this small hides the options behind a click and gives
 * no sense of what the axis even is — an operator opening the databases list
 * cannot tell that engine is a thing they may filter by until they open it.
 * Five labels laid out flat say what the platform runs and offer the filter in
 * the same gesture.
 *
 * The row scrolls sideways rather than wrapping. A wrapped row changes height
 * when a label is added, which moves the table down the page on some viewports
 * and not others; scrolling keeps the bar one line tall whatever it holds.
 *
 * Deliberately not a `<select>`-backed listbox with roving focus either. These
 * are links-to-a-filtered-view in everything but URL, and a plain group of
 * buttons is what a keyboard already knows how to walk.
 */
export function FilterChips<T extends string>({
  value,
  onChange,
  options,
  allLabel = "All",
  ariaLabel,
}: {
  /** The empty string is "no filter", which is why it is not part of `T`. */
  value: T | "";
  onChange: (value: T | "") => void;
  options: { value: T; label: string }[];
  allLabel?: string;
  ariaLabel: string;
}) {
  const chips: { value: T | ""; label: string }[] = [{ value: "", label: allLabel }, ...options];

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="scrollbar-slim -mx-1 flex max-w-full gap-1 overflow-x-auto px-1"
    >
      {chips.map((chip) => {
        const active = chip.value === value;
        return (
          <button
            key={chip.value || "all"}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(chip.value)}
            className={cx(
              /* Matches the control height the search input and pickers use, so
                 the filter bar reads as one row rather than three widgets that
                 happen to be adjacent. */
              "h-9 shrink-0 border px-3 text-sm whitespace-nowrap transition-colors duration-200 ease-smooth pointer-coarse:min-h-11",
              active
                ? "border-accent/40 bg-accent-soft/50 text-fg"
                : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
            )}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}
